/**
 * Contextual offers sheet — "Free delivery & all offers".
 *
 * Opened from the "View all offers" tab that sits above the contextual cart
 * strip (.cart-progressBar) on non-cart pages. The strip itself keeps showing
 * the tier/delivery message; this sheet lists every coupon behind it.
 *
 * Data sources (nothing here calls an API of its own):
 * - Coupons: the /v1/coupons/list response copy-code.js already fetches as soon
 *   as the cart has items. Read from window.__copyCodeCoupons (live) with the
 *   localStorage list cache as the cold-start fallback.
 * - Delivery row: theme settings (free shipping threshold + shipping fee),
 *   exactly the same source the strip uses today.
 *
 * Coupons cannot be applied outside the cart, so cards offer Copy (design:
 * "Copy" -> "Copied" + a "Go to Cart & Redeem Coupon" bar) instead of Apply.
 */
(function (window, document) {
  'use strict';

  var ROOT_ID = 'contextual_offers_root';
  var COUPONS_NS = '__copyCodeCoupons';
  var COUPON_LIST_CACHE_KEY = 'applied_coupon_list_cache';
  // Breathing room between the last card and the floating footer
  var LAST_CARD_GAP = 12;

  // Design asset. Locked and available differ only in fill, so the colour
  // comes from the card via currentColor (#a3a3a3 locked, #498ca6 available).
  var OFFER_ICON =
    '<svg class="ctx-offers-icon" width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M8.07155 0.0252327C9.73449 -0.175239 9.96435 0.861347 10.9964 1.629C11.4904 1.99572 12.2142 1.80503 12.7963 1.84903C13.9701 1.93705 14.8897 2.86117 14.9777 4.03466C15.0217 4.61652 14.8359 5.34017 15.1978 5.82914C15.7994 6.6408 16.714 6.95371 16.8118 8.17612C16.939 9.77987 15.9461 9.99503 15.1978 11.0023C14.8359 11.4863 15.0168 12.1904 14.9777 12.7625C14.8945 13.9605 13.9946 14.8895 12.7914 14.9775C12.2094 15.0215 11.4855 14.8357 10.9964 15.1975C10.1845 15.8038 9.87634 16.7133 8.64866 16.8111C7.03951 16.9431 6.82922 15.9456 5.82166 15.1975C5.33743 14.8357 4.63312 15.0166 4.06087 14.9775C2.86746 14.8943 1.93327 13.9898 1.84524 12.7918C1.80121 12.21 1.98707 11.4863 1.62514 10.9974C1.02354 10.1857 0.108913 9.8728 0.0110922 8.65039C-0.116075 7.04172 0.876806 6.83639 1.62514 5.82423C2.37347 4.81211 1.80121 4.60673 1.84524 4.02488C1.93327 2.82695 2.87235 1.92238 4.06577 1.84415C5.2592 1.76591 5.34234 1.98594 5.82653 1.62412C6.59932 1.04715 6.92701 0.157251 8.07642 0.0203429L8.07155 0.0252327ZM6.23741 5.30103C4.88259 5.49662 5.01952 7.66758 6.48196 7.63827C8.05685 7.60892 7.98836 5.04679 6.23741 5.30103ZM10.5269 5.63355C10.4681 5.64334 10.4094 5.6629 10.3557 5.68243L5.72873 10.2639C5.32764 10.836 6.05643 11.501 6.58954 11.0512L11.0844 6.56258C11.3877 6.13226 11.0355 5.56998 10.5269 5.63842V5.63355ZM10.2725 9.19315C8.71228 9.33495 8.903 11.7259 10.5513 11.5255C11.9795 11.3543 11.779 9.05135 10.2725 9.19315Z" fill="currentColor"/>' +
    '</svg>';

  // Design asset. The tick is part of the path, and the locked / unlocked
  // states differ only in fill, so the colour comes from the row via
  // currentColor (#a3a3a3 locked, #80a55e unlocked).
  var TRUCK_ICON =
    '<svg class="ctx-offers-icon truck-icon" width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M1.97073 3.51488e-05L5.90525 0.00128323C6.57139 0.00128323 7.23509 0.00262522 7.90518 0.00124571C8.67029 -0.000330863 9.32766 0.431482 9.53534 1.18769C9.59831 1.41707 9.59315 1.64102 9.59371 1.8767C9.59512 2.05428 9.59418 2.23188 9.59099 2.40944C9.85281 2.42482 10.2255 2.4124 10.4945 2.41176L12.3225 2.41217C12.8944 2.41797 13.0237 2.73931 13.3059 3.16279C13.5375 3.50845 13.7669 3.8556 13.994 4.20422L15.273 6.12247C15.4676 6.41457 15.6645 6.7068 15.8569 7.00025C15.8949 7.05796 15.9599 7.18287 15.9743 7.24969C16.0128 7.42687 16.0002 7.64402 15.9999 7.82814L15.9994 8.49819C15.999 9.14477 16.0016 9.79379 15.9997 10.4403C15.9985 10.8413 15.8026 11.279 15.51 11.5567C15.2019 11.8531 14.7869 12.0123 14.3597 11.9979C14.263 12.3753 14.1441 12.6565 13.8746 12.95C13.5225 13.3381 13.0308 13.5704 12.5076 13.5959C12.0509 13.6074 11.6679 13.5139 11.2853 13.2536C10.8543 12.9608 10.5565 12.5094 10.4567 11.998C10.0413 11.9913 9.61107 11.9969 9.1945 11.9971L6.92557 11.9967L6.05555 11.9976C5.89914 11.998 5.70264 12.0037 5.54911 11.9957C5.48752 12.2484 5.43287 12.4171 5.29481 12.6452C5.02044 13.0945 4.57974 13.4171 4.06863 13.5429C3.5517 13.6688 3.00585 13.5824 2.55314 13.3028C2.05788 12.9961 1.77568 12.5568 1.6452 11.9982C0.681041 12.0018 0.00571212 11.2967 0.00245574 10.3403L0.00265292 4.10473L0.00279368 2.34956C0.00264353 2.10342 -0.0113017 1.57853 0.0245466 1.35514C0.0760294 1.02286 0.232129 0.715651 0.470155 0.478141C0.923214 0.0254011 1.38136 -0.00115667 1.97073 3.51488e-05ZM4.00084 6.05593C3.66261 5.71423 3.2984 5.36652 2.96767 5.02152C2.59458 5.40068 2.17165 5.77877 1.81339 6.16778C2.20242 6.54774 2.58916 6.95887 2.97986 7.3318C3.2136 7.57298 3.44943 7.81219 3.68734 8.04933C3.76862 8.13041 3.91339 8.28422 3.99581 8.35489L7.78656 4.5659C7.65963 4.44059 6.67699 3.44375 6.63009 3.42665L4.00084 6.05593ZM12.4152 10.7983C12.1233 10.7951 11.8522 10.949 11.7053 11.2014C11.5585 11.4538 11.5586 11.7655 11.7058 12.0178C11.8529 12.27 12.1242 12.4237 12.4162 12.4201C12.8603 12.4147 13.2175 12.0529 13.2172 11.6087C13.2169 11.1646 12.8594 10.8032 12.4152 10.7983ZM12.4402 4.00107L10.9835 3.99842C10.891 3.99858 10.4581 4.00628 10.3991 3.99344L10.3962 5.57441L10.3978 6.12281C10.398 6.20511 10.4023 6.33096 10.3968 6.4095L12.8395 6.40979C13.212 6.40979 13.6176 6.41823 13.9862 6.40913L14.0215 6.40622L14.0294 6.39195C14.0087 6.32092 13.448 5.50955 13.3695 5.39176L12.7498 4.46182C12.6619 4.33043 12.53 4.11948 12.4402 4.00107ZM3.60814 10.7984C3.16166 10.7915 2.79358 11.1468 2.78467 11.5933C2.77576 12.0397 3.12937 12.4094 3.57578 12.4203C4.02503 12.4313 4.39759 12.075 4.40657 11.6256C4.41553 11.1763 4.05749 10.8053 3.60814 10.7984Z" fill="currentColor"/>' +
    '</svg>';

  // Design asset. Stroke is the design's own #727272, a shade darker than the
  // chip label, so it is not inherited from the chip's colour.
  var LOCK_ICON =
    '<svg width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M1.4 3.7V3.24286C1.4 1.7234 2.60429 0.5 4.1 0.5C5.59571 0.5 6.8 1.7234 6.8 3.24286V3.7M1.4 3.7C0.905 3.7 0.5 4.11143 0.5 4.61429V9.18571C0.5 9.68857 0.905 10.1 1.4 10.1H6.8C7.295 10.1 7.7 9.68857 7.7 9.18571V4.61429C7.7 4.11143 7.295 3.7 6.8 3.7M1.4 3.7H6.8M4.1 7.4V6.2" stroke="#727272" stroke-linecap="round"/>' +
    '</svg>';

  var CHEVRON_ICON =
    '<svg class="ctx-offer-card__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M2.5 4.25 6 7.75l3.5-3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var state = {
    // copiedCode marks which card shows "Copied": it holds until another code
    // is copied or the sheet closes. hasCopied keeps the "Go to Cart & Redeem
    // Coupon" bar up for the rest of the sheet session.
    copiedCode: '',
    hasCopied: false,
    listenersAttached: false,
    scrollY: 0,
  };

  function getConfig() {
    return window.contextualOffersConfig || {};
  }

  /**
   * A/B gate, read the same way as show_cart_fbt. The value is always a
   * string and only "true" enables the feature — missing, "false" or anything
   * else keeps it off. Re-read on every sync so a flag that lands after boot
   * takes effect on the next cart/coupon event.
   */
  function isEnabledByExperiment() {
    var flag =
      window.Shopify &&
      window.Shopify.abExperimentData &&
      window.Shopify.abExperimentData.show_contextual_offer_popup;
    return flag === 'true';
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text).replace(/[&<>"']/g, function (ch) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[ch];
    });
  }

  function formatRupees(cents) {
    var rupees = Math.max(0, Math.round(Number(cents) || 0) / 100);
    return '₹' + Math.round(rupees).toLocaleString('en-IN');
  }

  /* ---------------------------------------------------------------- data -- */

  function getCouponState() {
    return window[COUPONS_NS] || null;
  }

  function getCachedCouponList() {
    try {
      var parsed = JSON.parse(localStorage.getItem(COUPON_LIST_CACHE_KEY) || 'null');
      return parsed && Array.isArray(parsed.coupons) ? parsed.coupons : [];
    } catch (err) {
      return [];
    }
  }

  /** Coupons already fetched by copy-code.js; cache is the cold-start fallback. */
  function getCoupons() {
    var couponState = getCouponState();
    if (couponState) {
      if (Array.isArray(couponState.lastCouponList) && couponState.lastCouponList.length) {
        return couponState.lastCouponList;
      }
      if (Array.isArray(couponState.viewAllCoupons) && couponState.viewAllCoupons.length) {
        return couponState.viewAllCoupons;
      }
    }
    return getCachedCouponList();
  }

  function getCart() {
    var couponState = getCouponState();
    var cart = couponState && couponState.lastCartData;
    return cart && typeof cart.item_count !== 'undefined' ? cart : null;
  }

  function getCartItemCount() {
    var cart = getCart();
    if (cart) return Number(cart.item_count) || 0;
    var config = getConfig();
    return Number(config.initialItemCount) || 0;
  }

  function getFreeShippingThresholdCents() {
    var config = getConfig();
    var fromUi = window.__copyCodeCouponUi && window.__copyCodeCouponUi.freeShippingThresholdCents;
    return Number(config.freeShippingThresholdCents) || Number(fromUi) || 39900;
  }

  function getShippingFeeCents() {
    var config = getConfig();
    var fromUi = window.__copyCodeCouponUi && window.__copyCodeCouponUi.shippingFeeCents;
    return Number(config.shippingFeeCents) || Number(fromUi) || 4000;
  }

  /**
   * Free-delivery state from theme settings + live cart, matching the
   * threshold comparison used elsewhere in the theme (calcGrandTotal).
   */
  function getDeliveryOffer() {
    var cart = getCart();
    var totals =
      cart && typeof window.getCartTotals === 'function' ? window.getCartTotals(cart) : null;
    var threshold, fee, net, unlocked;

    if (totals) {
      // The cart drawer's own shipping decision: coupon-adjusted subtotal
      // against the threshold, with the drawer's fee and threshold.
      threshold = totals.freeShippingThresholdCents;
      fee = totals.shippingFeeCents;
      net = totals.netSubtotalCents;
      unlocked = totals.shippingCents === 0;
    } else {
      // copy-code.js not on the page: same rule from the raw cart + settings.
      threshold = getFreeShippingThresholdCents();
      fee = getShippingFeeCents();
      net = cart ? Number(cart.total_price) || 0 : 0;
      unlocked = net > threshold;
    }

    if (unlocked) {
      return {
        unlocked: true,
        title: 'Free delivery unlocked',
        message: 'Saved ' + formatRupees(fee),
      };
    }
    // Free delivery needs the subtotal strictly above the threshold, so the
    // shopper needs one paisa past it; round up so the amount shown unlocks it.
    var remainingRupees = Math.ceil((threshold - net + 1) / 100);
    return {
      unlocked: false,
      title: 'Unlock your free delivery',
      message: 'Add ₹' + remainingRupees.toLocaleString('en-IN') + ' more to get FREE delivery',
    };
  }

  function isApplicable(coupon) {
    return !!coupon && coupon.isApplicable !== false;
  }

  function getSavingsLine(coupon) {
    var raw =
      coupon.savingsMessage ||
      coupon.savings_message ||
      coupon.highlightMessage ||
      coupon.highlight_message ||
      coupon.extraSavingText ||
      coupon.extra_saving_text ||
      coupon.subtitle ||
      coupon.subTitle ||
      '';
    raw = String(raw || '').trim();
    if (raw) return raw;
    var message = String(coupon.message || '').trim();
    return /^save\b/i.test(message) || /\bwith this (offer|code)\b/i.test(message) ? message : '';
  }

  /** Applicable cards lead with the savings line, locked ones with the unlock hint. */
  function getCardMessage(coupon) {
    var savings = getSavingsLine(coupon);
    var message = String(coupon.message || '').trim();
    return isApplicable(coupon) ? savings || message : message || savings;
  }

  function getDetailBullets(coupon) {
    var raw = coupon.additionalDetails || coupon.additional_details;
    if (!Array.isArray(raw)) return [];
    return raw
      .map(function (line) {
        return String(line || '').trim();
      })
      .filter(Boolean);
  }

  /**
   * Design groups the list as "Best offer" (the top applicable offer) and
   * "Other offers" (everything else) — same split the cart sheet uses.
   */
  function groupCoupons(coupons) {
    var best = [];
    var other = [];
    (coupons || []).forEach(function (coupon) {
      if (!coupon || !coupon.code) return;
      if (!best.length && isApplicable(coupon)) best.push(coupon);
      else other.push(coupon);
    });
    return { best: best, other: other };
  }

  /* --------------------------------------------------------------- render -- */

  /** Currency amounts are emphasised inside offer copy, as in the design. */
  function withBoldAmounts(text) {
    return escapeHtml(text).replace(
      /(?:₹|Rs\.?\s?)[\d,]+(?:\.\d+)?/g,
      function (amount) {
        return '<strong>' + amount + '</strong>';
      }
    );
  }

  function renderLockChip() {
    return '<span class="ctx-offer-lock">' + LOCK_ICON + 'Locked</span>';
  }

  function renderDeliveryRow() {
    var offer = getDeliveryOffer();
    return (
      '<div class="ctx-offer-delivery' +
      (offer.unlocked ? ' ctx-offer-delivery--unlocked' : '') +
      '">' +
      TRUCK_ICON +
      '<div class="ctx-offer-delivery__text">' +
      '<div class="ctx-offer-delivery__title">' +
      escapeHtml(offer.title) +
      '</div>' +
      '<div class="ctx-offer-delivery__message">' +
      withBoldAmounts(offer.message) +
      '</div>' +
      '</div>' +
      (offer.unlocked ? '' : renderLockChip()) +
      '</div>'
    );
  }

  function renderCard(coupon) {
    var code = String(coupon.code || '');
    var available = isApplicable(coupon);
    var message = getCardMessage(coupon);
    var bullets = getDetailBullets(coupon);
    var isCopied = available && state.copiedCode && state.copiedCode === code;

    var footer = available
      ? '<button type="button" class="ctx-offer-card__copy' +
        (isCopied ? ' is-copied' : '') +
        '" data-ctx-copy="' +
        escapeHtml(code) +
        '">' +
        (isCopied ? 'Copied' : 'Copy') +
        '</button>'
      : renderLockChip();

    return (
      '<article class="ctx-offer-card' +
      (available ? ' ctx-offer-card--available' : '') +
      '" data-ctx-code="' +
      escapeHtml(code) +
      '">' +
      '<div class="ctx-offer-card__main">' +
      OFFER_ICON +
      '<div class="ctx-offer-card__text">' +
      '<h3 class="ctx-offer-card__title">' +
      escapeHtml(coupon.title || '') +
      '</h3>' +
      (message
        ? '<p class="ctx-offer-card__message">' + withBoldAmounts(message) + '</p>'
        : '') +
      (bullets.length ? renderDetailsToggle('open', 'View more details') : '') +
      '</div>' +
      '</div>' +
      (bullets.length ? renderDetailsBand(bullets) : '') +
      '<div class="ctx-offer-card__footer">' +
      '<span class="ctx-offer-card__code">' +
      escapeHtml(code.toUpperCase()) +
      '</span>' +
      footer +
      '</div>' +
      '</article>'
    );
  }

  /**
   * Expanding moves the toggle: collapsed it sits under the message, expanded
   * it sits at the foot of the details band. Two buttons, one shown at a time,
   * rather than one button that moves — keeps the markup static.
   */
  function renderDetailsToggle(variant, label) {
    return (
      '<button type="button" class="ctx-offer-card__more ctx-offer-card__more--' +
      variant +
      '" data-ctx-details aria-expanded="' +
      (variant === 'close' ? 'true' : 'false') +
      '">' +
      '<span>' +
      label +
      '</span>' +
      CHEVRON_ICON +
      '</button>'
    );
  }

  /** Own band between two dashed rules, full width — not inset under the title. */
  function renderDetailsBand(bullets) {
    return (
      '<div class="ctx-offer-card__details-band">' +
      '<p class="ctx-offer-card__details">' +
      escapeHtml(bullets.join(' | ')) +
      '</p>' +
      renderDetailsToggle('close', 'View less details') +
      '</div>'
    );
  }

  function renderSection(title, coupons) {
    if (!coupons.length) return '';
    return (
      '<section class="ctx-offers-section">' +
      '<h2 class="ctx-offers-section__title">' +
      escapeHtml(title) +
      '</h2>' +
      coupons.map(renderCard).join('') +
      '</section>'
    );
  }

  function buildBodyHtml() {
    var grouped = groupCoupons(getCoupons());
    return (
      renderDeliveryRow() +
      renderSection('Best offer', grouped.best) +
      renderSection('Other offers', grouped.other)
    );
  }

  /* ----------------------------------------------------------------- sheet -- */

  function ensureSheet() {
    var root = document.getElementById(ROOT_ID);
    if (root) return root;

    root = document.createElement('div');
    root.id = ROOT_ID;
    root.hidden = true;
    root.innerHTML =
      '<div class="ctx-offers-overlay" aria-hidden="true">' +
      '<button type="button" class="ctx-offers-close" data-ctx-close aria-label="Close offers">' +
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '<div class="ctx-offers-sheet" role="dialog" aria-modal="true" aria-label="Free delivery and all offers">' +
      '<div class="ctx-offers-header">Free delivery &amp; all offers</div>' +
      '<div class="ctx-offers-body" data-ctx-body></div>' +
      '<div class="ctx-offers-cta" data-ctx-cta hidden>' +
      '<p class="ctx-offers-cta__text">Go to cart &amp; redeem coupon</p>' +
      '<a class="ctx-offers-cta__btn" data-ctx-cart-link href="/cart">' +
      '<span class="ctx-offers-cta__thumb" data-ctx-cart-thumb aria-hidden="true"></span>' +
      '<span class="ctx-offers-cta__label">' +
      '<span class="ctx-offers-cta__title">View cart</span>' +
      '<span class="ctx-offers-cta__count" data-ctx-cart-count></span>' +
      '</span>' +
      '<span class="ctx-offers-cta__arrow" aria-hidden="true">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M4 1.5 8.5 6 4 10.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '</span>' +
      '</a>' +
      '</div>' +
      '</div>' +
      '</div>';

    document.body.appendChild(root);
    attachSheetListeners(root);
    return root;
  }

  function attachSheetListeners(root) {
    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-ctx-cart-link]')) {
        // Open the cart drawer like the floating "View cart" pill rather than
        // navigating; the href stays as the fallback when there is no drawer.
        event.preventDefault();
        track('view_cart_from_contextual');
        // Product team wants the standard view_cart alongside the contextual
        // one, so this button reports into the same funnel as every other
        // route into the cart. click_from tells the two sources apart.
        if (typeof window.fireViewCart === 'function') {
          window.fireViewCart('contextual_offers');
        }
        openCartDrawer();
        return;
      }

      if (event.target.closest('[data-ctx-close]')) {
        close();
        return;
      }

      var copyBtn = event.target.closest('[data-ctx-copy]');
      if (copyBtn) {
        copyCode(copyBtn.getAttribute('data-ctx-copy'));
        return;
      }

      var detailsBtn = event.target.closest('[data-ctx-details]');
      if (detailsBtn) {
        toggleDetails(detailsBtn);
        return;
      }

      // Backdrop click (the overlay itself, never the sheet).
      if (event.target.classList.contains('ctx-offers-overlay')) close();
    });
  }

  function toggleDetails(button) {
    var card = button.closest('.ctx-offer-card');
    if (!card) return;
    var expanded = card.classList.toggle('is-expanded');
    Array.prototype.forEach.call(
      card.querySelectorAll('[data-ctx-details]'),
      function (toggle) {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    );
  }

  function isOpen() {
    var root = document.getElementById(ROOT_ID);
    return !!root && !root.hidden;
  }

  function syncCta(root) {
    var cta = root.querySelector('[data-ctx-cta]');
    if (!cta) return;
    if (!state.hasCopied) {
      cta.classList.remove('is-visible');
      cta.hidden = true;
      applyFooterRoom(root);
      return;
    }
    cta.hidden = false;

    var count = getCartItemCount();
    var countEl = root.querySelector('[data-ctx-cart-count]');
    if (countEl) countEl.textContent = count + (count === 1 ? ' Item' : ' Items');
    var link = root.querySelector('[data-ctx-cart-link]');
    if (link) link.setAttribute('href', getConfig().cartUrl || '/cart');

    var thumb = root.querySelector('[data-ctx-cart-thumb]');
    if (thumb) {
      var image = getFirstCartItemImage();
      thumb.innerHTML = image
        ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy">'
        : '';
    }

    applyFooterRoom(root);

    // Flush the off-screen position before switching it on, so the browser
    // has a start frame to slide from instead of snapping to the end state.
    void cta.offsetHeight;
    cta.classList.add('is-visible');
  }

  /**
   * The footer floats over the list, so whatever sits under it can only be
   * reached by scrolling. Padding the list by the footer's height gives that
   * room — but on a short list it also made the content taller, which grew the
   * sheet upward the moment the footer appeared. So the list's height is
   * pinned to what it is without the padding first: same sheet, extra scroll.
   */
  function applyFooterRoom(root) {
    var body = root.querySelector('[data-ctx-body]');
    var cta = root.querySelector('[data-ctx-cta]');
    if (!body || !cta) return;

    // Always measure unpinned, so repeated calls don't compound.
    body.style.maxHeight = '';
    publishRevealPad(root, 0);
    if (cta.hidden) return;

    var naturalHeight = body.getBoundingClientRect().height;
    body.style.maxHeight = naturalHeight + 'px';
    publishRevealPad(root, cta.offsetHeight + LAST_CARD_GAP);
  }

  /** Extra scroll room under the list, so the footer never covers content for good. */
  function publishRevealPad(root, height) {
    var sheet = root.querySelector('.ctx-offers-sheet');
    if (sheet) sheet.style.setProperty('--ctx-reveal-pad', height + 'px');
  }

  /** Matches the floating pill, which leads with the first line item's image. */
  function getFirstCartItemImage() {
    var cart = getCart();
    var item = cart && cart.items && cart.items[0];
    if (!item) return '';
    var url = item.image || (item.featured_image && item.featured_image.url) || '';
    return String(url || '');
  }

  function renderBody() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    var body = root.querySelector('[data-ctx-body]');
    if (body) body.innerHTML = buildBodyHtml();
    syncCta(root);
  }

  /**
   * Patch the copy buttons in place rather than re-rendering the body — a full
   * render would collapse any "View more details" the shopper has opened.
   */
  function syncCopyButtons() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll('[data-ctx-copy]'), function (button) {
      var isCopied = button.getAttribute('data-ctx-copy') === state.copiedCode;
      button.classList.toggle('is-copied', isCopied);
      button.textContent = isCopied ? 'Copied' : 'Copy';
    });
    syncCta(root);
  }

  /**
   * The footer floating over the list is deliberate — cards showing through
   * the glass is the design. But the last card has nothing below it to scroll
   * up into view, so only that one is nudged clear, and only when its own
   * Copy is tapped. Every other card is left exactly where it is.
   */
  function revealCopiedLastCard(code) {
    var root = document.getElementById(ROOT_ID);
    if (!root || root.hidden) return;
    var body = root.querySelector('[data-ctx-body]');
    var cta = root.querySelector('[data-ctx-cta]');
    if (!body || !cta || cta.hidden) return;

    var cards = body.querySelectorAll('.ctx-offer-card');
    var last = cards[cards.length - 1];
    if (!last || last.getAttribute('data-ctx-code') !== code) return;

    // offsetTop, not a rect: the footer may still be sliding up, and its live
    // rect would put it lower than it is about to rest, under-measuring the
    // overlap. offsetTop is layout, so it already holds the resting position.
    var sheet = root.querySelector('.ctx-offers-sheet');
    if (!sheet) return;
    var ctaTop = sheet.getBoundingClientRect().top + cta.offsetTop;

    var overlap = last.getBoundingClientRect().bottom + LAST_CARD_GAP - ctaTop;
    if (overlap <= 0) return;

    // applyFooterRoom already added the room to scroll it clear. Set the
    // position directly rather than animating: a smooth scroll is skipped
    // while the page is hidden and under reduced-motion, which would leave
    // the card covered.
    body.scrollTop = body.scrollTop + overlap;
  }

  function copyCode(code) {
    if (!code) return;

    writeToClipboard(code);
    track('copy_code', { coupon: code });

    // Optimistic, like Shopify.copyCodeFn elsewhere in the theme: a clipboard
    // permission quirk must not leave the shopper without the "Copied" state
    // or the go-to-cart bar. The code stays on screen either way.
    // No timeout: "Copied" stays on this card until another code is copied
    // (syncCopyButtons resets the others) or the sheet closes.
    state.copiedCode = code;
    state.hasCopied = true;
    syncCopyButtons();
    revealCopiedLastCard(code);
  }

  function writeToClipboard(code) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).catch(function () {
        legacyCopy(code);
      });
      return;
    }
    legacyCopy(code);
  }

  /* ------------------------------------------------------------ analytics -- */

  /**
   * Same two sinks as custom_events.liquid: a plain dataLayer push, and
   * MoEngage via window.trackME (which queues if the SDK hasn't loaded yet).
   */
  function track(eventName, params) {
    var payload = params || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
      if (typeof window.trackME === 'function') {
        window.trackME(eventName, payload);
      } else if (window.Moengage && typeof window.Moengage.track_event === 'function') {
        window.Moengage.track_event(eventName, payload);
      }
    } catch (err) {
      console.warn('[contextual-offers] track failed:', eventName, err);
    }
  }

  /**
   * Rupees — the "To pay" the shopper sees (applied coupon, shipping and the
   * prepaid discount included), via copy-code.js. Falls back to the raw
   * Shopify cart total only if that script isn't on the page.
   */
  function getCartValue() {
    var cart = getCart();
    if (!cart) return 0;
    if (typeof window.getCartTotals === 'function') {
      var totals = window.getCartTotals(cart);
      if (totals) return totals.toPayCents / 100;
    }
    return (Number(cart.total_price) || 0) / 100;
  }

  /** execCommand fallback: the clipboard API needs permission some in-app browsers withhold. */
  function legacyCopy(code) {
    var input = document.createElement('textarea');
    input.value = code;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      /* nothing else to try */
    }
    document.body.removeChild(input);
  }

  /**
   * `body { overflow: hidden }` alone does not hold on iOS Safari — the page
   * keeps scrolling behind the sheet. Pinning the body at its current offset
   * does, as long as the offset is restored on close.
   */
  function lockScroll() {
    state.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = -state.scrollY + 'px';
    document.body.classList.add('ctx-offers-open');
  }

  function unlockScroll() {
    if (!document.body.classList.contains('ctx-offers-open')) return;
    document.body.classList.remove('ctx-offers-open');
    document.body.style.top = '';
    // Jump back, never animate, in case anything sets scroll-behavior: smooth.
    var root = document.documentElement;
    var previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, state.scrollY || 0);
    root.style.scrollBehavior = previousBehavior;
  }

  /**
   * Mirrors startOpenCart + openShopifyCartDrawer in custom-sticky-cart.js so
   * the drawer opens exactly as it does from the floating pill. That script
   * only loads when the sticky cart setting is on, so the open is repeated
   * here; the loader is reused from window.HdtCartDrawerFeedback when present.
   */
  function openCartDrawer() {
    close();

    var feedback = window.HdtCartDrawerFeedback;
    if (feedback && typeof feedback.show === 'function') {
      feedback.show();
      // Same 2s safety net as the pill, cancelled once the drawer opens.
      var safetyTimer = setTimeout(feedback.hide, 2000);
      var onDrawerOpen = function (event) {
        if (event.target && event.target.id === 'CartDrawer') {
          clearTimeout(safetyTimer);
          document.removeEventListener('dialog:afterOpen', onDrawerOpen);
        }
      };
      document.addEventListener('dialog:afterOpen', onDrawerOpen);
    }

    var afterNextPaint =
      feedback && typeof feedback.afterNextPaint === 'function'
        ? feedback.afterNextPaint
        : function (fn) {
            requestAnimationFrame(fn);
          };
    afterNextPaint(openDrawerElement);
  }

  function openDrawerElement() {
    var drawer = document.querySelector('hdt-cart-drawer');
    if (drawer && typeof drawer.openDialog === 'function') {
      drawer.openDialog(false);
      return;
    }
    if (drawer) {
      drawer.setAttribute('open', 'immediate');
      return;
    }
    // No drawer: go to the cart page. Deliberately not clicking the cart icon
    // like the pill does — custom_events.liquid fires view_cart on that click,
    // which would be a second view_cart on top of the one fired above.
    window.location.href = getConfig().cartUrl || '/cart';
  }

  function open() {
    if (!isEnabledByExperiment() || !getCartItemCount()) return;
    var root = ensureSheet();
    renderBody();
    root.hidden = false;
    var overlay = root.querySelector('.ctx-offers-overlay');
    if (overlay) {
      overlay.setAttribute('aria-hidden', 'false');
      // Flush the closed position so the browser has something to animate
      // from, then start the slide. A requestAnimationFrame here never ran
      // when frames were paused, leaving the sheet stuck mid-fade.
      void overlay.offsetHeight;
      overlay.classList.add('is-open');
    }
    lockScroll();
  }

  function close() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    var overlay = root.querySelector('.ctx-offers-overlay');
    if (overlay) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    }
    root.hidden = true;
    unlockScroll();
    var body = root.querySelector('[data-ctx-body]');
    if (body) body.style.maxHeight = '';
    publishRevealPad(root, 0);
    state.copiedCode = '';
    state.hasCopied = false;
    // Reset with the sheet, so a reopen starts the footer off-screen again.
    var cta = root.querySelector('[data-ctx-cta]');
    if (cta) {
      cta.classList.remove('is-visible');
      cta.hidden = true;
    }
  }

  /* ------------------------------------------------------------------- tab -- */

  /** The tab is only meaningful with a non-empty cart and at least one offer. */
  function syncTab() {
    var tab = document.querySelector('[data-ctx-offers-tab]');
    if (!tab) return;
    var show =
      isEnabledByExperiment() && getCartItemCount() > 0 && getCoupons().length > 0;
    // custom-sticky-cart.js watches this attribute to restack the "View cart"
    // pill, which sits directly above the tab.
    tab.hidden = !show;
    if (!show && isOpen()) close();
  }

  function sync() {
    syncTab();
    if (isOpen()) renderBody();
  }

  function attachGlobalListeners() {
    if (state.listenersAttached) return;
    state.listenersAttached = true;

    document.addEventListener('click', function (event) {
      // Anywhere on the strip opens the sheet, not just the tab (which sits
      // inside it, so this still fires once). Inert while the tab is hidden —
      // flag off, empty cart or no offers — exactly as the strip behaved before.
      var strip = event.target.closest('.cart-progressBar');
      if (!strip) return;
      var tab = strip.querySelector('[data-ctx-offers-tab]');
      if (!tab || tab.hidden) return;
      event.preventDefault();
      track('contextual_cta_click', { cart_value: getCartValue() });
      open();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });

    window.addEventListener(
      'resize',
      function () {
        var root = document.getElementById(ROOT_ID);
        if (root && !root.hidden) applyFooterRoom(root);
      },
      { passive: true }
    );

    ['cart:change', 'cart:refresh', 'coupons:list-updated'].forEach(function (name) {
      document.addEventListener(name, function () {
        // Let copy-code.js land its cart/coupon state before reading it.
        setTimeout(sync, 0);
      });
    });
  }

  window.ContextualOffers = {
    open: open,
    close: close,
    sync: sync,
    isOpen: isOpen,
  };

  attachGlobalListeners();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})(window, document);
